// src/oura/ui/Steps.tsx
//
// A dated list on a thin line: what is done, where we are, what comes next.
// The merchant's plan (one line a month) and any short timeline use it.

import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { FOCUS_RING, LATE_TEXT, TYPE } from "./tokens";

export type StepState = "done" | "now" | "next" | "later";

export interface StepItem {
    key: string;
    title: ReactNode;
    meta?: ReactNode;
    /** A late note after the meta, in rose. */
    late?: ReactNode;
    state: StepState;
    /** Makes the row a button (the month opens its drawer). */
    onClick?: () => void;
}

const DOT: Record<StepState, string> = {
    // Filled green: finished.
    done: "bg-emerald-500",
    // Filled blue with a pale halo: you are here.
    now: "bg-blue-500 shadow-[0_0_0_3px_#E1E9FE]",
    // Hollow blue: coming, and planned.
    next: "bg-white shadow-[inset_0_0_0_2px_#2F66FE]",
    // Hollow grey: later in the term.
    later: "bg-white shadow-[inset_0_0_0_2px_#D1D5DB]",
};

function StepRow({ item, last }: { item: StepItem; last: boolean }) {
    const grey = item.state === "later";
    const body = (
        <>
            <span aria-hidden className={cn("relative z-[1] mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", DOT[item.state])} />
            <span className="min-w-0 flex-1">
                <span
                    className={cn(
                        TYPE.tb,
                        "block font-medium",
                        grey ? "text-gray-500" : "text-gray-900",
                        item.onClick && "group-hover:text-blue-600",
                    )}
                >
                    {item.title}
                </span>
                {(item.meta || item.late) && (
                    <span className={cn(TYPE.tm, "block", grey ? "text-gray-400" : "text-gray-500")}>
                        {item.meta}
                        {item.meta && item.late && " · "}
                        {item.late && <span className={LATE_TEXT}>{item.late}</span>}
                    </span>
                )}
            </span>
        </>
    );
    return (
        // 14px between rows: a dated list reads line by line, never as one block.
        <li className="relative pb-3.5 last:pb-0">
            {/* The line to the next dot: from this dot's centre (11px down) to
                the next one's, the same 11px into the next row. Drawn per row,
                so it ends on the last dot however tall the last row is. */}
            {!last && <span aria-hidden className="absolute -bottom-[11px] left-1 top-[11px] w-px bg-gray-200" />}
            {item.onClick ? (
                <button type="button" onClick={item.onClick} className={cn("group flex w-full gap-3 rounded-md text-left", FOCUS_RING)}>
                    {body}
                </button>
            ) : (
                <div className="flex gap-3">{body}</div>
            )}
        </li>
    );
}

export function Steps({ items, className }: { items: StepItem[]; className?: string }) {
    return (
        <div className={cn("relative", className)}>
            <ol className="list-plain m-0 p-0">
                {items.map((item, i) => (
                    <StepRow key={item.key} item={item} last={i === items.length - 1} />
                ))}
            </ol>
        </div>
    );
}
